<template lang="pug">
yz-dialog(v-model="showDialog" fullscreen persistent fluid scrollable max-width="1000")
  yz-card
    yz-dialog-fullscreen-header(:title="dialogTitle" @back="onClose" @cancel="onClose" @save="onSave" :loading="isLoading")
      template(v-if="isEditMode && isBranchMode" v-slot:header-append)
        yz-btn.mr-2(icon="$yzi-trash-03" outlined icon-color="yz-danger-500" :loading="isDeleting" @click="onDelete")
    yz-dialog-fullscreen-content
      //- Loading state while fetching details
      template(v-if="isFetchingDetails")
        div(style="max-width: 850px; margin: 0 auto;")
          yz-card.mb-4(outlined)
            .pa-4
              yz-skeleton-loader(type="text")
          yz-card(outlined)
            .pa-4
              yz-skeleton-loader(type="list-item-three-line")

      //- Main content
      template(v-else)
        v-form(ref="form" v-model="isFormValid")
          div(style="max-width: 850px; margin: 0 auto;")
            //- Storage location details card
            yz-card.mb-4(outlined)
              .pa-4
                .yz-heading-8.text-neutral-700.mb-3 Storage location details
                .yz-heading-9.text-neutral-700.mb-1 Name
                yz-text-field(v-model="form.name" placeholder="Name" required style="max-width: 560px;")

            //- Add item to this location card
            yz-card(outlined)
              .pa-4
                .yz-heading-8.text-neutral-700.mb-1 Add item to this location

                //- Info message for new warehouse mode
                template(v-if="isNewWarehouse")
                  yz-card.mt-3.pa-4(outlined)
                    .d-flex.align-center
                      v-icon.mr-3(color="primary") $yzi-info-circle
                      div
                        .yz-medium-body-sm.text-neutral-700 Create warehouse first
                        .yz-regular-body-xs.text-neutral-500 You can add items to this storage location after creating the warehouse.

                //- Normal flow for existing branches
                template(v-else)
                  .d-flex.align-center.mb-4
                    .yz-regular-body-sm.text-neutral-700 Search items you want to add from your inventory list.
                    yz-btn.ml-1(label="Or create new item" underline append-icon="$yzi-arrow-up-right" @click="onCreateNewItem")

                  //- Empty state
                  yz-empty-state-card(v-if="selectedItems.length === 0" icon="$yzi-colors" title="No items selected" description="Any selected items from your list will show up here")

                  //- Items table
                  yz-data-table(v-else :headers="itemHeaders" :items="selectedItems" header-class="yz-medium-body-xs" body-class="yz-regular-body-sm")
                    template(v-slot:item.name="{ item }")
                      span.font-medium {{ item.name }}
                    template(v-slot:item.uom="{ item }")
                      span {{ item.uom }}
                    template(v-slot:item.actions="{ item }")
                      yz-btn(icon="$yzi-trash-03" outlined icon-color="yz-danger-500" @click="onRemoveItem(item)")

                  //- Action buttons
                  .d-flex.align-center.mt-4
                    yz-btn(label="Search from inventory" outlined, size="lg" @click="onSearchInventory")
                    .mx-5.yz-heading-8 OR
                    yz-btn(label="Add from your recent purchases/lists" outlined, size="lg" @click="onAddFromRecent")

    //- Dialogs
    add-item-to-list-dialog(v-if="openAddItemToListDialog" :open.sync="openAddItemToListDialog" :override-company-slug="branchSlug" @added="handleAddItemToList")
    select-recent-purchases-lists-dialog(v-if="openSelectRecentPurchasesListsDialog" :open.sync="openSelectRecentPurchasesListsDialog" :override-company-slug="branchSlug" @added="handleAddItemToList")
    add-new-medicine-dialog(v-if="openAddNewMedicineDialog" :open.sync="openAddNewMedicineDialog" :override-company-slug="branchSlug" :override-company-id="branchId" @created="handleNewMedicineCreated" @closed="openAddNewMedicineDialog = false")
    yz-delete-confirmation-dialog(v-model="showDeleteConfirmDialog" item="storage location" description="This action will permanently delete the storage location and remove all items from it. This cannot be undone." :loading="isDeleting" @confirmed="confirmDelete" @canceled="cancelDelete")
      yz-card.mt-4.pa-4(v-if="storageLocation" outlined)
        .yz-medium-body-sm.text-neutral-700 {{ storageLocation.value || storageLocation.name }}
        .yz-regular-body-xs.text-neutral-500.mt-2(v-if="selectedItems.length > 0") {{ selectedItems.length }} item(s) will be removed from this location
</template>

<script>
import { mapActions } from 'vuex'
import AddItemToListDialog from '@/components/clinic/dialogs/purchasehub/AddItemToListDialog.vue'
import SelectRecentPurchasesListsDialog from '@/components/clinic/dialogs/purchasehub/SelectRecentPurchasesListsDialog.vue'
import AddNewMedicineDialog from '@/components/hq/inventory/branches-warehouses/dialog/AddNewMedicineDialog.vue'

export default {
  components: {
    AddItemToListDialog,
    SelectRecentPurchasesListsDialog,
    AddNewMedicineDialog
  },
  props: {
    open: {
      type: Boolean,
      default: false
    },
    selectedBranch: {
      type: Object,
      default: null
    },
    storageLocation: {
      type: Object,
      default: null
    },
    isNewWarehouse: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      form: {
        name: ''
      },
      selectedItems: [],
      isFormValid: false,
      isLoading: false,
      isDeleting: false,
      isFetchingDetails: false,
      showDeleteConfirmDialog: false,
      openAddItemToListDialog: false,
      openSelectRecentPurchasesListsDialog: false,
      openAddNewMedicineDialog: false,
      itemHeaders: [
        { text: 'Item', value: 'name', sortable: false },
        { text: 'UOM', value: 'uom', sortable: false },
        { text: '', value: 'actions', sortable: false, align: 'end', width: '60px' }
      ]
    }
  },
  computed: {
    showDialog: {
      get() {
        return this.open
      },
      set(value) {
        this.$emit('update:open', value)
      }
    },
    isEditMode() {
      return !!this.storageLocation?.id
    },
    isBranchMode() {
      // True when we have a branch slug AND not creating a new warehouse
      // Skip API calls when isNewWarehouse is true (warehouse doesn't exist yet)
      return !!this.branchSlug && !this.isNewWarehouse
    },
    branchSlug() {
      return this.selectedBranch?.slug
    },
    branchId() {
      return this.selectedBranch?.id
    },
    dialogTitle() {
      return this.isEditMode ? 'Edit storage' : 'Add storage'
    }
  },
  mounted() {
    this.initializeForm()
  },
  methods: {
    ...mapActions(['showMessage']),
    async initializeForm() {
      if (this.isEditMode && this.isBranchMode) {
        await this.fetchStorageLocationDetails()
      }
    },
    async fetchStorageLocationDetails() {
      this.isFetchingDetails = true
      try {
        const { data } = await this.$axios.get(`/clinics/${this.branchSlug}/inventories/locations/${this.storageLocation.id}`, { params: { expand: 'products' } })
        this.form.name = data.value || ''
        this.selectedItems = (data.products || []).map(p => ({
          id: p.id,
          name: p.name || '',
          uom: p.uom || ''
        }))
      } catch (error) {
        console.error('Error fetching storage location details:', error)
        this.showMessage(['error', 'Failed to load storage location details'])
        // Fallback to prop data
        this.form.name = this.storageLocation.value || this.storageLocation.name || ''
        this.selectedItems = (this.storageLocation.products || []).map(p => ({
          id: p.id || p,
          name: p.name || '',
          uom: p.uom || ''
        }))
      } finally {
        this.isFetchingDetails = false
      }
    },
    onClose() {
      this.showDialog = false
    },
    async onSave() {
      if (!this.$refs.form.validate()) {
        this.showMessage(['error', 'Please fill in all required fields'])
        return
      }

      this.isLoading = true

      try {
        if (this.isBranchMode) {
          // Call API directly for existing branches
          const payload = {
            value: this.form.name,
            products: this.selectedItems.map(item => item.id)
          }

          if (this.isEditMode) {
            // PATCH existing storage location
            await this.$axios.patch(`/clinics/${this.branchSlug}/inventories/locations/${this.storageLocation.id}`, payload)
            this.showMessage(['success', 'Storage location updated'])
          } else {
            // POST new storage location
            await this.$axios.post(`/clinics/${this.branchSlug}/inventories/locations/`, {
              company: this.branchId,
              field: 'medication.location',
              ...payload
            })
            this.showMessage(['success', 'Storage location added'])
          }
        }

        // Emit saved event for parent to handle (refresh data or update local state)
        this.$emit('saved', {
          name: this.form.name,
          items: this.selectedItems,
          id: this.storageLocation?.id
        })
        this.showDialog = false
      } catch (error) {
        console.error('Error saving storage location:', error)
        const errorMessage = error.response?.data?.detail || 'Failed to save storage location'
        this.showMessage(['error', errorMessage])
      } finally {
        this.isLoading = false
      }
    },
    onDelete() {
      if (!this.isEditMode || !this.isBranchMode) return
      this.showDeleteConfirmDialog = true
    },
    async confirmDelete() {
      this.isDeleting = true
      try {
        await this.$axios.delete(`/clinics/${this.branchSlug}/inventories/locations/${this.storageLocation.id}`)
        this.showMessage(['success', 'Storage location deleted'])
        this.$emit('deleted', this.storageLocation.id)
        this.showDeleteConfirmDialog = false
        this.showDialog = false
      } catch (error) {
        console.error('Error deleting storage location:', error)
        const errorMessage = error.response?.data?.detail || 'Failed to delete storage location'
        this.showMessage(['error', errorMessage])
      } finally {
        this.isDeleting = false
      }
    },
    cancelDelete() {
      this.showDeleteConfirmDialog = false
    },
    onSearchInventory() {
      this.openAddItemToListDialog = true
    },
    onAddFromRecent() {
      this.openSelectRecentPurchasesListsDialog = true
    },
    onCreateNewItem() {
      this.openAddNewMedicineDialog = true
    },
    handleNewMedicineCreated(item) {
      if (item && item.product) {
        const newItem = {
          id: item.product.id,
          name: item.product.name,
          uom: item.product.uom || ''
        }
        const existingIds = this.selectedItems.map(i => i.id)
        if (!existingIds.includes(newItem.id)) {
          this.selectedItems.push(newItem)
        }
      }
      this.openAddNewMedicineDialog = false
    },
    onRemoveItem(item) {
      const index = this.selectedItems.findIndex(i => i.id === item.id)
      if (index > -1) {
        this.selectedItems.splice(index, 1)
      }
    },
    handleAddItemToList(items) {
      const newItems = items.map(item => ({
        id: item.id,
        name: item.name,
        uom: item.uom
      }))
      const existingIds = this.selectedItems.map(i => i.id)
      const uniqueNewItems = newItems.filter(item => !existingIds.includes(item.id))
      this.selectedItems = [...this.selectedItems, ...uniqueNewItems]
      this.openAddItemToListDialog = false
      this.openSelectRecentPurchasesListsDialog = false
    }
  }
}
</script>

<style lang="scss" scoped></style>
